import { ExportDialog } from '@aibees/elephant/components/export-dialog';
import { QuickActionsButtonGroupLabelsMenu } from '@aibees/elephant/components/quick-actions-button-group-labels-menu';
import { QuickActionsButtonGroupListsMenu } from '@aibees/elephant/components/quick-actions-button-group-lists-menu';
import { useTableActionContext } from '@aibees/elephant/context';
import { useActionButtonContext } from '@aibees/elephant/context/action-button-context';
import { usePersonCompanyInfo } from '@aibees/elephant/hooks/people/use-person-company-info';
import { useHandleFindButton } from '@aibees/elephant/hooks/use-handle-find-button';
import { SearchType } from '@aibees/elephant/types';
import { useFullScreenLoadingContext } from '@aibees/shared/context';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { memo, useCallback, useState } from 'react';
import { QuickActionMenuItem } from '../quick-action-menu-item';
import { QuickActionsButtonGroupExportMenu } from '../quick-actions-button-group-export-menu';
import { AssignOwnerDialog } from '../search-table-action-dialog-more/components/search-table-action-assign-owner-dialog';
import * as Styled from './styles';
import * as SX from './sx';
import type { PeopleQuickActionsProps as Props } from './types';

export const PeopleQuickActions = memo(({ className, handleClose }: Props) => {
  const { item } = useActionButtonContext();
  const { id: personCompanyId } = usePersonCompanyInfo(item);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { ownerDialogState, setIntegrationConfig } = useTableActionContext();
  const labelsMenuOpen = useBoolean();
  const listsMenuOpen = useBoolean();
  const exportMenuOpen = useBoolean();
  const { createSearchListAndRedirect } = useHandleFindButton({
    type: SearchType.People,
  });
  const { setTitle: setLoadingScreenTitle, state: loadingScreenState } =
    useFullScreenLoadingContext();

  const handleClickFindCompanies = () => {
    if (personCompanyId) {
      loadingScreenState.onTrue();
      setLoadingScreenTitle('Redirecting...');
      createSearchListAndRedirect([personCompanyId]);
    }
  };
  const handleClickExport = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      setAnchorEl(event.currentTarget);
      exportMenuOpen.onTrue();
      setIntegrationConfig(undefined);
    },
    [exportMenuOpen, setIntegrationConfig]
  );

  function handleClickAssignOwner() {
    ownerDialogState.onTrue();
    handleClose?.();
  }

  return (
    <Styled.Wrapper
      className={classnames('PeopleQuickActions', className)}
      sx={SX.root}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <QuickActionMenuItem
        title="Export"
        icon="export"
        onClick={handleClickExport}
        hasChildren
        childState={exportMenuOpen}
      />

      <QuickActionMenuItem
        title="Find Companies"
        icon="business"
        onClick={handleClickFindCompanies}
        disabled={!personCompanyId}
      />
      <QuickActionMenuItem
        title="Lists"
        icon="list"
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          listsMenuOpen.onTrue();
        }}
        hasChildren
        childState={listsMenuOpen}
      />
      <QuickActionMenuItem
        title="Labels"
        icon="label"
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          labelsMenuOpen.onTrue();
        }}
        hasChildren
        childState={labelsMenuOpen}
      />
      <QuickActionMenuItem
        title="Assign Owner"
        icon="account-tag"
        onClick={handleClickAssignOwner}
      />

      <QuickActionsButtonGroupListsMenu
        state={listsMenuOpen}
        anchorEl={anchorEl}
        handleClose={handleClose}
      />
      <QuickActionsButtonGroupLabelsMenu
        state={labelsMenuOpen}
        anchorEl={anchorEl}
        handleClose={handleClose}
      />

      <QuickActionsButtonGroupExportMenu
        state={exportMenuOpen}
        anchorEl={anchorEl}
        handleClose={handleClose}
      />

      <ExportDialog key="export" formId="people-quick-actions-export-dialog" />
      <AssignOwnerDialog />
    </Styled.Wrapper>
  );
});

PeopleQuickActions.displayName = 'PeopleQuickActions';
